import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { debounce } from 'lodash'

// antd
import { Col, Input, Dropdown, Row, Skeleton, Typography } from 'antd'

// slices
import {
    projectsState,
    browseProjectsListForClient,
    resetBrowseProjectsList,
    setActiveProjectSupport,
} from './projects.slice'
import { loginState } from '../login/login.slice'

// routes
import { buildProjectTaskTabSearch, PRIVATE_ROUTES } from '../../routes'

// components
import CreateProjectModal from './components/CreateProjectModal'
import HeaderPrimaryButton from '../../components/HeaderPrimaryButton'

// styles
import styles from './browseProjects.module.css'
import { harmonicIconToneForBackground } from '../../helpers/harmonicIconColor'

const { Text, Title } = Typography

/** Pastel tile backgrounds (reference: soft blue, mint, pink, purple) */
const TILE_BG = [
    '#6ba3f5',
    '#5cc9b3',
    '#e88bc2',
    '#9b8adb',
    '#f0ae5f',
    '#7eb8ea',
    '#c796e8',
]

const TILE_ICONS = [
    'mdi:chart-box-outline',
    'mdi:rocket-launch-outline',
    'mdi:format-list-bullets',
    'mdi:view-dashboard-outline',
    'mdi:clipboard-text-outline',
    'mdi:lightbulb-on-outline',
    'mdi:folder-outline',
]

const hashString = (str) => {
    let hash = 0
    for (let i = 0; i < str.length; i++)
        hash = str.charCodeAt(i) + ((hash << 5) - hash)
    return Math.abs(hash)
}

const getTileStyle = (name) => {
    const h = hashString(name || 'x')
    return {
        backgroundColor: TILE_BG[h % TILE_BG.length],
        icon: TILE_ICONS[h % TILE_ICONS.length],
    }
}

const STATUS_LABELS = {
    active: 'Active',
    archived: 'Archived',
}

const BrowseProjectsPage = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { userProfile } = useSelector(loginState)
    const {
        browseProjectsData = [],
        browseProjectsListStatus,
        browseProjectsPage,
        browseProjectsIsNext,
    } = useSelector(projectsState)

    const [search, setSearch] = useState('') // UI-only (instant filter)
    const [apiSearch, setApiSearch] = useState('') // debounced (API calls)
    const [createOpen, setCreateOpen] = useState(false)
    const [statusFilter, setStatusFilter] = useState(null)

    const PAGE_LIMIT = 50

    const fetchBrowseList = useCallback(() => {
        if (!userProfile) return
        dispatch(resetBrowseProjectsList())
        dispatch(
            browseProjectsListForClient({
                skip: 0,
                filter: {},
                ...(statusFilter != null
                    ? { isArchived: statusFilter === 'archived' }
                    : {}),
                limit: PAGE_LIMIT,
                search: apiSearch,
                userProfile,
            })
        )
    }, [dispatch, userProfile, statusFilter, apiSearch])

    useEffect(() => {
        fetchBrowseList()
    }, [fetchBrowseList])

    const filtered = useMemo(() => {
        if (!search) return browseProjectsData
        return browseProjectsData.filter((p) =>
            p?.name?.toLowerCase().includes(search.toLowerCase())
        )
    }, [browseProjectsData, search])

    const loading = browseProjectsListStatus === 'loading'
    const showSkeleton = loading && filtered.length === 0

    const loadMore = useCallback(() => {
        if (!userProfile) return
        if (browseProjectsListStatus === 'loading') return
        if (!browseProjectsIsNext) return

        dispatch(
            browseProjectsListForClient({
                skip: browseProjectsPage * PAGE_LIMIT,
                filter: {},
                ...(statusFilter != null
                    ? { isArchived: statusFilter === 'archived' }
                    : {}),
                limit: PAGE_LIMIT,
                search: apiSearch,
                userProfile,
            })
        )
    }, [
        dispatch,
        userProfile,
        browseProjectsListStatus,
        browseProjectsIsNext,
        browseProjectsPage,
        statusFilter,
        apiSearch,
    ])

    const handleListScroll = useCallback(
        (e) => {
            const el = e.currentTarget
            if (!el) return
            const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
            if (remaining < 140) loadMore()
        },
        [loadMore]
    )

    const onOpenProject = (id) => {
        if (!userProfile?.isSuperAdmin) {
            dispatch(setActiveProjectSupport({ projectId: id }))
        }
        const isMemberUser = userProfile?.isExistingUser
        if (isMemberUser) {
            navigate(
                {
                    pathname: PRIVATE_ROUTES.projects.projectIdView(
                        id,
                        'board'
                    ),
                    search: buildProjectTaskTabSearch('development'),
                },
                { replace: true }
            )
        } else {
            navigate(PRIVATE_ROUTES.projects.projectId(id), {
                replace: true,
            })
        }
    }

    const statusMenu = useMemo(
        () => ({
            items: [
                { key: 'active', label: 'Active' },
                { key: 'archived', label: 'Archived' },
            ],
            onClick: ({ key, domEvent }) => {
                domEvent?.stopPropagation?.()
                setStatusFilter(key)
            },
        }),
        []
    )

    const filterTrigger = (label) => (
        <button type="button" className={styles.pillBtn}>
            <span className={styles.pillLabel}>{label}</span>
            <span className={styles.pillChevron}>
                <Icon icon="mdi:chevron-down" width={18} height={18} />
            </span>
        </button>
    )

    const clearStatusFilter = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
        setStatusFilter(null)
    }, [])

    const debouncedSetApiSearch = useMemo(
        () =>
            debounce((val) => {
                setApiSearch(val)
            }, 500),
        []
    )

    useEffect(() => {
        return () => {
            debouncedSetApiSearch.cancel()
        }
    }, [debouncedSetApiSearch])

    return (
        <div className={styles.pageContainer}>
            <div className={styles.pageHeader}>
                <Row
                    justify="space-between"
                    align="middle"
                    wrap
                    gutter={[16, 12]}
                >
                    <Col flex="none">
                        <Title level={3} className={styles.pageTitle}>
                            Browse projects
                        </Title>
                    </Col>
                    <Col flex="1 1 280px" style={{ minWidth: 0 }}>
                        <div className={styles.controls}>
                            <div className={styles.searchWrap}>
                                <Input
                                    allowClear
                                    placeholder="Find a project"
                                    className={styles.search}
                                    prefix={
                                        <Icon
                                            icon="mdi:magnify"
                                            width={18}
                                            height={18}
                                        />
                                    }
                                    onChange={(e) => {
                                        const val = e.target.value || ''
                                        setSearch(val)
                                        debouncedSetApiSearch(val)
                                    }}
                                    value={search}
                                />
                            </div>
                            <div className={styles.filterTriggers}>
                                <Dropdown
                                    menu={statusMenu}
                                    trigger={['click']}
                                    getPopupContainer={(n) =>
                                        n?.parentElement ?? document.body
                                    }
                                >
                                    {statusFilter ? (
                                        <div
                                            className={styles.statusFilterChip}
                                            role="group"
                                            aria-label={`Status filter: ${STATUS_LABELS[statusFilter]}`}
                                        >
                                            <span
                                                className={
                                                    styles.statusFilterChipLabel
                                                }
                                            >
                                                {STATUS_LABELS[statusFilter]}
                                            </span>
                                            <button
                                                type="button"
                                                className={
                                                    styles.statusFilterChipClear
                                                }
                                                onClick={clearStatusFilter}
                                                aria-label="Clear status filter"
                                            >
                                                <Icon
                                                    icon="mdi:close"
                                                    width={16}
                                                    height={16}
                                                />
                                            </button>
                                        </div>
                                    ) : (
                                        filterTrigger('Status')
                                    )}
                                </Dropdown>
                            </div>
                            {userProfile &&
                                (userProfile?.isSuperAdmin ||
                                    userProfile?.isExistingUser) && (
                                    <HeaderPrimaryButton
                                        onClick={() => setCreateOpen(true)}
                                    >
                                        Create Project
                                    </HeaderPrimaryButton>
                                )}
                        </div>
                    </Col>
                </Row>
            </div>

            <section className={styles.listSection} aria-label="Project list">
                <div className={styles.listHeader}>
                    <div>Name</div>
                    <div>Key</div>
                    <div>Project Type</div>
                    <div className={styles.colSort}>
                        Last Modified
                        {/* <span className={styles.sortIcon} aria-hidden>
                            <Icon
                                icon="mdi:arrow-up-down"
                                width={14}
                                height={14}
                            />
                        </span> */}
                    </div>
                </div>

                <div className={styles.listScroll} onScroll={handleListScroll}>
                    <div className={styles.listBody}>
                        {showSkeleton ? (
                            Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className={`${styles.listRow} ${styles.skeletonRow}`}
                                >
                                    <div className={styles.projectCell}>
                                        <Skeleton.Avatar
                                            active
                                            size={28}
                                            shape="square"
                                            style={{
                                                borderRadius: '8px',
                                            }}
                                        />
                                        <Skeleton.Input
                                            active
                                            size="small"
                                            style={{ width: 200 }}
                                        />
                                    </div>
                                    <div className={styles.keyCell}>
                                        <Skeleton.Input
                                            active
                                            size="small"
                                            style={{ width: 64, height: 16 }}
                                        />
                                    </div>
                                    <div className={styles.typeCell}>
                                        <Skeleton.Input
                                            active
                                            size="small"
                                            style={{ width: 92, height: 16 }}
                                        />
                                    </div>
                                    <div className={styles.listFooter}>
                                        <Skeleton.Input
                                            active
                                            size="small"
                                            style={{ width: 72, height: 16 }}
                                        />
                                    </div>
                                </div>
                            ))
                        ) : filtered.length === 0 ? (
                            <div className={styles.empty}>
                                <div className={styles.emptyIcon} aria-hidden>
                                    <Icon
                                        icon="mdi:folder-search-outline"
                                        width={26}
                                        height={26}
                                    />
                                </div>
                                <p className={styles.emptyTitle}>
                                    No projects match
                                </p>
                                <p className={styles.emptyHint}>
                                    {search
                                        ? 'Try another search, or clear the box to see all projects.'
                                        : statusFilter
                                          ? 'No projects for this status. Clear the filter or pick another option.'
                                          : 'Create your first project to get started.'}
                                </p>
                            </div>
                        ) : (
                            filtered.map((project) => {
                                const tile = getTileStyle(project?.name)
                                const keyVal = project?.projectKey?.trim()
                                const projectColor =
                                    project?.colorCode || tile.backgroundColor

                                return (
                                    <button
                                        key={project._id}
                                        type="button"
                                        className={styles.listRow}
                                        onClick={() =>
                                            onOpenProject(project._id)
                                        }
                                    >
                                        <div className={styles.projectCell}>
                                            <div
                                                className={styles.projectAvatar}
                                                style={{
                                                    backgroundColor:
                                                        projectColor,
                                                }}
                                            >
                                                {project?.urlType === 'icon' &&
                                                project?.url ? (
                                                    <Icon
                                                        icon={project.url}
                                                        width={18}
                                                        height={18}
                                                        style={{
                                                            color: harmonicIconToneForBackground(
                                                                projectColor
                                                            ),
                                                        }}
                                                    />
                                                ) : project?.urlType ===
                                                      'upload' &&
                                                  project?.url ? (
                                                    <img
                                                        src={project.url}
                                                        alt={project.name || ''}
                                                        className={
                                                            styles.projectAvatarImage
                                                        }
                                                    />
                                                ) : !project?.colorCode &&
                                                  project?.projectProfile ? (
                                                    <img
                                                        src={
                                                            project.projectProfile
                                                        }
                                                        alt={project.name || ''}
                                                        className={
                                                            styles.projectAvatarImage
                                                        }
                                                    />
                                                ) : !project?.colorCode ? (
                                                    <Text
                                                        className={
                                                            styles.projectAvatarInitial
                                                        }
                                                    >
                                                        {project?.name
                                                            ?.charAt(0)
                                                            ?.toUpperCase()}
                                                    </Text>
                                                ) : null}
                                            </div>
                                            <div className={styles.projectMeta}>
                                                <Text
                                                    className={
                                                        styles.projectName
                                                    }
                                                    title={project?.name}
                                                >
                                                    {project?.name}
                                                </Text>
                                                <span
                                                    className={styles.joinedTag}
                                                >
                                                    Joined
                                                </span>
                                            </div>
                                        </div>
                                        <div className={styles.keyCell}>
                                            <span
                                                className={
                                                    keyVal
                                                        ? `${styles.keyBadge} ${styles.keyBadgeTinted}`
                                                        : `${styles.keyBadge} ${styles.keyBadgeEmpty}`
                                                }
                                                style={
                                                    keyVal
                                                        ? {
                                                              '--key-accent':
                                                                  projectColor,
                                                          }
                                                        : undefined
                                                }
                                                title={keyVal || undefined}
                                            >
                                                <span
                                                    className={
                                                        keyVal
                                                            ? styles.keyText
                                                            : styles.keyTextPlaceholder
                                                    }
                                                >
                                                    {keyVal || '—'}
                                                </span>
                                            </span>
                                        </div>
                                        <div className={styles.typeCell}>
                                            <Text className={styles.typeText}>
                                                {project?.projectType?.trim() ||
                                                    '—'}
                                            </Text>
                                        </div>
                                        <div className={styles.lastModified}>
                                            {project?.updated ? (
                                                <span>
                                                    {new Date(
                                                        project.updated
                                                    ).toLocaleDateString()}
                                                </span>
                                            ) : (
                                                '—'
                                            )}
                                        </div>
                                    </button>
                                )
                            })
                        )}
                    </div>
                </div>
            </section>

            <CreateProjectModal
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                listMode="browseProjects"
            />
        </div>
    )
}

export default BrowseProjectsPage
